import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import PageHeader from '../../../components/layout/PageHeader';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../../components/ui/breadcrumb';
import MarkdownMessage from '../../../components/ui/markdown-message';
import {
  ArrowLeft, ShieldCheck, Target, Briefcase, FileText, RefreshCw, TrendingUp,
  Loader2, CheckCircle, XCircle, AlertTriangle, ChevronDown, ChevronUp,
  Send, Download, PieChart, Activity, Sparkles, ArrowUpCircle, ArrowDownCircle,
} from 'lucide-react';
import { useAdvisorStore } from '../../../state/advisorStore';

const RISK_COLORS = {
  Conservative: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  Moderate: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  Aggressive: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  'Very Aggressive': 'bg-red-500/10 text-red-600 border-red-500/30',
};

function formatCorpus(v) {
  if (!v) return '—';
  if (v >= 10_000_000) return `₹${(v / 10_000_000).toFixed(2)} Cr`;
  if (v >= 100_000) return `₹${(v / 100_000).toFixed(2)} L`;
  return `₹${v.toLocaleString('en-IN')}`;
}

const RISK_QUESTIONS = [
  { id: 'investment_horizon', q: 'Investment time horizon?', opts: [
    { value: 'less_than_1y', label: '< 1 year' }, { value: '1_3y', label: '1-3 years' },
    { value: '3_5y', label: '3-5 years' }, { value: '5_10y', label: '5-10 years' },
    { value: 'more_than_10y', label: '10+ years' },
  ]},
  { id: 'loss_tolerance', q: 'If portfolio dropped 20%, what would you do?', opts: [
    { value: 'sell_all', label: 'Sell everything' }, { value: 'sell_some', label: 'Sell some' },
    { value: 'hold', label: 'Hold and wait' }, { value: 'buy_more', label: 'Buy more' },
  ]},
  { id: 'income_stability', q: 'How stable is your income?', opts: [
    { value: 'unstable', label: 'Irregular' }, { value: 'moderate', label: 'Moderate' },
    { value: 'very_stable', label: 'Very stable' }, { value: 'retired_passive', label: 'Retired/Passive' },
  ]},
  { id: 'market_experience', q: 'Equity market experience?', opts: [
    { value: 'none', label: 'None' }, { value: '1_3y', label: '1-3 years' },
    { value: '3_7y', label: '3-7 years' }, { value: '7_plus', label: '7+ years' },
  ]},
  { id: 'return_expectation', q: 'Expected annual return?', opts: [
    { value: '8_10', label: '8-10%' }, { value: '10_15', label: '10-15%' },
    { value: '15_20', label: '15-20%' }, { value: '20_plus', label: '20%+' },
  ]},
  { id: 'portfolio_percentage', q: 'What % of net worth is this investment?', opts: [
    { value: 'less_10', label: '< 10%' }, { value: '10_30', label: '10-30%' },
    { value: '30_60', label: '30-60%' }, { value: '60_plus', label: '60%+' },
  ]},
  { id: 'liquidity_need', q: 'When might you need this money?', opts: [
    { value: 'anytime', label: 'Anytime' }, { value: '1_2y', label: 'Not before 1-2y' },
    { value: '3_5y', label: 'Not before 3-5y' }, { value: '5_plus', label: '5+ years' },
  ]},
  { id: 'concentration_comfort', q: 'Comfort with concentrated bets?', opts: [
    { value: 'not_at_all', label: 'Prefer diversification' }, { value: 'somewhat', label: 'Somewhat' },
    { value: 'very', label: 'Very comfortable' },
  ]},
];

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const {
    activeClient, activeClientLoading, activeClientError, fetchClient, clearActiveClient,
    submitRiskProfile, riskResult, riskLoading, fetchSuitability, suitability,
    proposals, proposalsLoading, fetchProposals, generateProposal, updateProposalStatus,
    modelPortfolios, fetchModelPortfolios,
    rebalanceResult, rebalanceLoading, rebalanceClient,
    mandate, fetchMandate, saveMandate,
    submitProposalReview, enablePortal, disablePortal, portalLink,
    fetchReviews, generateReview, sendReview, reviews,
    fetchAttribution, attribution, attributionLoading,
    clientHoldings, clientHoldingsLoading, fetchClientHoldings,
    dailyReport, dailyReportLoading, fetchLatestDailyReport, generateDailyReport, fetchDailyReports, dailyReports,
  } = useAdvisorStore();

  const [tab, setTab] = useState('overview');
  const [riskAnswers, setRiskAnswers] = useState({});
  const [genLoading, setGenLoading] = useState(false);
  const [selectedMP, setSelectedMP] = useState('');
  const [expandedProposal, setExpandedProposal] = useState(null);
  const [mForm, setMForm] = useState({
    max_single_stock_pct: '', max_sector_pct: '', excluded_sectors: '', excluded_isins: '',
  });
  const [reviewPeriod, setReviewPeriod] = useState('Q4 FY25');

  useEffect(() => {
    if (!mandate) return;
    setMForm({
      max_single_stock_pct: mandate.max_single_stock_pct ?? '',
      max_sector_pct: mandate.max_sector_pct ?? '',
      excluded_sectors: (mandate.excluded_sectors || []).join(', '),
      excluded_isins: (mandate.excluded_isins || []).join(', '),
    });
  }, [mandate]);

  useEffect(() => {
    fetchClient(clientId);
    fetchProposals(clientId);
    fetchModelPortfolios();
    fetchMandate(clientId);
    fetchReviews(clientId);
    fetchClientHoldings(clientId);
    return () => clearActiveClient();
  }, [clientId, fetchClient, fetchProposals, fetchModelPortfolios, clearActiveClient, fetchMandate, fetchReviews, fetchClientHoldings]);

  useEffect(() => {
    if (tab === 'attribution') fetchAttribution(clientId);
    if (tab === 'daily') { fetchLatestDailyReport(clientId); fetchDailyReports(clientId); }
  }, [tab, clientId, fetchAttribution, fetchLatestDailyReport, fetchDailyReports]);

  const [acceptResult, setAcceptResult] = useState(null);
  const handleAcceptProposal = useCallback(async (proposalId) => {
    setAcceptResult(null);
    const result = await updateProposalStatus(proposalId, 'accepted');
    if (result?.holdings_added > 0) {
      setAcceptResult({ id: proposalId, count: result.holdings_added });
    }
    fetchProposals(clientId);
    fetchClientHoldings(clientId);
  }, [clientId, updateProposalStatus, fetchProposals, fetchClientHoldings]);

  const client = activeClient;

  const handleRiskSubmit = useCallback(async () => {
    try { await submitRiskProfile(clientId, riskAnswers); } catch {}
  }, [clientId, riskAnswers, submitRiskProfile]);

  const handleGenerate = useCallback(async () => {
    setGenLoading(true);
    try {
      await generateProposal(clientId, { modelPortfolioId: selectedMP || null, corpus: client?.corpus });
    } catch {}
    setGenLoading(false);
  }, [clientId, selectedMP, client, generateProposal]);

  const handleRebalance = useCallback(async () => {
    try { await rebalanceClient(clientId, selectedMP || null); } catch {}
  }, [clientId, selectedMP, rebalanceClient]);

  if (activeClientLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>;
  }

  if (activeClientError || !client) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate('/public/clients')} className="text-muted-foreground hover:text-foreground" aria-label="Back to clients">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <PageHeader title="Client" subtitle="Unable to load" />
        </div>
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {activeClientError || 'Client not found or you do not have access.'}
          </CardContent>
        </Card>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Briefcase },
    { id: 'daily', label: 'Daily Intelligence', icon: Activity },
    { id: 'risk', label: 'Risk Profile', icon: ShieldCheck },
    { id: 'mandate', label: 'Mandate', icon: Target },
    { id: 'proposals', label: 'Proposals', icon: FileText },
    { id: 'rebalancing', label: 'Rebalancing', icon: RefreshCw },
    { id: 'reviews', label: 'Reviews', icon: FileText },
    { id: 'attribution', label: 'Attribution', icon: PieChart },
    { id: 'portal', label: 'Portal', icon: Send },
  ];

  return (
    <div className="space-y-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/public/clients">Clients</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{client.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => navigate('/public/clients')} className="text-muted-foreground hover:text-foreground" aria-label="Back to clients">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <PageHeader title={client.name} subtitle={client.email || 'No email'} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-card/50 rounded-lg w-fit">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === t.id ? 'bg-emerald-500/10 text-emerald-500' : 'text-muted-foreground hover:text-foreground'}`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Corpus</p>
              <p className="text-xl font-bold">{formatCorpus(client.corpus)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Risk Bucket</p>
              <p className="text-xl font-bold">{client.risk_bucket || 'Not Assessed'}</p>
              {client.risk_score != null && <p className="text-xs text-muted-foreground">Score: {client.risk_score}/100</p>}
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Investment Goal</p>
              <p className="text-sm font-medium">{client.investment_goal || '—'}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Proposals</p>
              <p className="text-xl font-bold">{client.proposals?.length || 0}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Holdings Tracked</p>
              <p className="text-xl font-bold">{clientHoldings.length}</p>
            </CardContent></Card>
          </div>

          {client.notes && (
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Notes</p>
              <p className="text-sm">{client.notes}</p>
            </CardContent></Card>
          )}

          {/* Portfolio Holdings */}
          {clientHoldingsLoading && (
            <Card><CardContent className="py-8 text-center">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
            </CardContent></Card>
          )}

          {!clientHoldingsLoading && clientHoldings.length > 0 && (
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-emerald-500" />
                    Tracked Portfolio ({clientHoldings.length} stocks)
                  </h3>
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => setTab('daily')}>
                    <Activity className="w-3 h-3 mr-1" /> Daily Analysis
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left py-2 pr-3 font-medium">Symbol</th>
                        <th className="text-left py-2 px-2 font-medium">Company</th>
                        <th className="text-right py-2 px-2 font-medium">Qty</th>
                        <th className="text-right py-2 px-2 font-medium">Avg Cost</th>
                        <th className="text-right py-2 px-2 font-medium">Value (est.)</th>
                        <th className="text-left py-2 pl-2 font-medium">Sector</th>
                        <th className="text-left py-2 pl-2 font-medium">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientHoldings.map((h, i) => {
                        const value = (h.quantity || 0) * (h.avg_buy_price || 0);
                        return (
                          <tr key={h.id || i} className="border-b border-border/50 hover:bg-secondary/20">
                            <td className="py-1.5 pr-3 font-semibold text-emerald-500">{h.symbol}</td>
                            <td className="py-1.5 px-2 text-muted-foreground truncate max-w-[150px]">{h.company_name || '—'}</td>
                            <td className="text-right py-1.5 px-2">{(h.quantity || 0).toLocaleString('en-IN')}</td>
                            <td className="text-right py-1.5 px-2">₹{(h.avg_buy_price || 0).toLocaleString('en-IN')}</td>
                            <td className="text-right py-1.5 px-2 font-medium">₹{value.toLocaleString('en-IN')}</td>
                            <td className="py-1.5 pl-2 text-muted-foreground">{h.sector || '—'}</td>
                            <td className="py-1.5 pl-2">
                              {h.source?.startsWith('proposal:') ? (
                                <Badge variant="outline" className="text-[9px] px-1 py-0 border-emerald-500/30 text-emerald-500">Proposal</Badge>
                              ) : (
                                <span className="text-muted-foreground">{h.source || '—'}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {!clientHoldingsLoading && clientHoldings.length === 0 && (
            <Card><CardContent className="py-8 text-center text-muted-foreground">
              <Briefcase className="w-8 h-8 mx-auto opacity-30 mb-2" />
              <p className="text-sm">No holdings tracked yet.</p>
              <p className="text-xs mt-1">Generate and accept a proposal to add stocks to this client's portfolio.</p>
            </CardContent></Card>
          )}
        </div>
      )}

      {/* Risk Profile Tab */}
      {tab === 'risk' && (
        <div className="space-y-4">
          {client.risk_bucket && (
            <Card className={`border ${RISK_COLORS[client.risk_bucket] || ''}`}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-6 h-6" />
                  <div>
                    <p className="font-semibold">{client.risk_bucket}</p>
                    <p className="text-xs text-muted-foreground">Risk Score: {client.risk_score}/100</p>
                  </div>
                </div>
                {riskResult?.ideal_allocation && (
                  <div className="mt-3 grid grid-cols-4 gap-2">
                    {Object.entries(riskResult.ideal_allocation).map(([k, v]) => (
                      <div key={k} className="text-center">
                        <p className="text-lg font-bold">{v}%</p>
                        <p className="text-[10px] text-muted-foreground">{k.replace('_', ' ')}</p>
                      </div>
                    ))}
                  </div>
                )}
                <Button size="sm" variant="outline" className="mt-3" onClick={() => fetchSuitability(clientId)}>
                  Check Suitability
                </Button>
                {suitability?.warnings?.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {suitability.warnings.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-amber-600">
                        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{w}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="pt-4">
              <h3 className="text-sm font-semibold mb-4">Risk Assessment Questionnaire</h3>
              <div className="space-y-4">
                {RISK_QUESTIONS.map(q => (
                  <div key={q.id}>
                    <p className="text-sm font-medium mb-2">{q.q}</p>
                    <div className="flex flex-wrap gap-2">
                      {q.opts.map(o => (
                        <button key={o.value} onClick={() => setRiskAnswers(a => ({ ...a, [q.id]: o.value }))}
                          className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${riskAnswers[q.id] === o.value ? 'bg-emerald-500/10 border-emerald-500 text-emerald-600' : 'border-border hover:border-emerald-500/30'}`}>
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <Button size="sm" className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                disabled={riskLoading || Object.keys(riskAnswers).length < 4}
                onClick={handleRiskSubmit}>
                {riskLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
                Calculate Risk Profile
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {tab === 'mandate' && (
        <Card>
          <CardContent className="pt-4 space-y-3">
            <h3 className="text-sm font-semibold">Investment policy (mandate)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <label className="text-xs text-muted-foreground">Max single stock %</label>
                <Input value={mForm.max_single_stock_pct} onChange={e => setMForm(f => ({ ...f, max_single_stock_pct: e.target.value }))} type="number" className="mt-1" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Max sector %</label>
                <Input value={mForm.max_sector_pct} onChange={e => setMForm(f => ({ ...f, max_sector_pct: e.target.value }))} type="number" className="mt-1" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-muted-foreground">Excluded sectors (comma-separated)</label>
                <Input value={mForm.excluded_sectors} onChange={e => setMForm(f => ({ ...f, excluded_sectors: e.target.value }))} className="mt-1" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-muted-foreground">Excluded ISINs (comma-separated)</label>
                <Input value={mForm.excluded_isins} onChange={e => setMForm(f => ({ ...f, excluded_isins: e.target.value }))} className="mt-1" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={async () => {
                await saveMandate(clientId, {
                  max_single_stock_pct: mForm.max_single_stock_pct ? parseFloat(mForm.max_single_stock_pct) : null,
                  max_sector_pct: mForm.max_sector_pct ? parseFloat(mForm.max_sector_pct) : null,
                  excluded_sectors: mForm.excluded_sectors ? mForm.excluded_sectors.split(',').map(s => s.trim()).filter(Boolean) : [],
                  excluded_isins: mForm.excluded_isins ? mForm.excluded_isins.split(',').map(s => s.trim().toUpperCase()).filter(Boolean) : [],
                });
              }}>Save mandate</Button>
              <Button size="sm" variant="outline" onClick={async () => {
                await saveMandate(clientId, {
                  max_single_stock_pct: mForm.max_single_stock_pct ? parseFloat(mForm.max_single_stock_pct) : null,
                  max_sector_pct: mForm.max_sector_pct ? parseFloat(mForm.max_sector_pct) : null,
                  excluded_sectors: mForm.excluded_sectors ? mForm.excluded_sectors.split(',').map(s => s.trim()).filter(Boolean) : [],
                  excluded_isins: mForm.excluded_isins ? mForm.excluded_isins.split(',').map(s => s.trim().toUpperCase()).filter(Boolean) : [],
                  signed_at: new Date().toISOString(),
                });
              }}>Sign mandate</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Proposals Tab */}
      {tab === 'proposals' && (
        <div className="space-y-4">
          <Card><CardContent className="pt-4">
            <h3 className="text-sm font-semibold mb-3">Generate New Proposal</h3>
            <div className="flex items-center gap-3">
              <select value={selectedMP} onChange={e => setSelectedMP(e.target.value)}
                className="rounded-md border bg-background px-3 py-2 text-sm flex-1 max-w-xs">
                <option value="">No model portfolio (AI will suggest)</option>
                {modelPortfolios.map(mp => (
                  <option key={mp.id} value={mp.id}>{mp.name} ({mp.risk_bucket})</option>
                ))}
              </select>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                disabled={genLoading} onClick={handleGenerate}>
                {genLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-4 h-4 mr-1" />}
                {genLoading ? 'Generating...' : 'Generate Proposal'}
              </Button>
            </div>
            {genLoading && <p className="text-xs text-muted-foreground mt-2">AI is generating personalized stock picks. This may take 1-2 minutes...</p>}
          </CardContent></Card>

          {proposalsLoading && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-emerald-500" /></div>}

          {proposals.map(p => {
            const proposal = p.proposal || {};
            const isExpanded = expandedProposal === p.id;
            return (
              <Card key={p.id} className="overflow-hidden">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm">{formatCorpus(p.corpus)} Portfolio Proposal</p>
                        <Badge className={`text-[10px] ${
                          p.status === 'accepted' ? 'bg-emerald-500/10 text-emerald-600' :
                          p.status === 'rejected' ? 'bg-red-500/10 text-red-600' :
                          p.status === 'sent' ? 'bg-blue-500/10 text-blue-600' :
                          'bg-gray-500/10 text-gray-600'
                        }`}>{p.status}</Badge>
                        {p.approval_status && (
                          <Badge variant="outline" className="text-[10px] capitalize">{p.approval_status.replace('_', ' ')}</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {p.status === 'draft' && (p.approval_status || 'draft') === 'draft' && (
                        <Button size="sm" variant="secondary" onClick={async () => { await submitProposalReview(p.id); fetchProposals(clientId); }}>
                          Submit for review
                        </Button>
                      )}
                      {p.status === 'draft' && (p.approval_status || 'draft') === 'approved' && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => updateProposalStatus(p.id, 'sent')}>
                            <Send className="w-3 h-3 mr-1" />Send to client
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleAcceptProposal(p.id)}>
                            <CheckCircle className="w-3 h-3 mr-1" />Accept
                          </Button>
                          {acceptResult?.id === p.id && (
                            <Badge className="bg-emerald-500/10 text-emerald-500 text-[10px]">
                              {acceptResult.count} stocks added to portfolio
                            </Badge>
                          )}
                        </>
                      )}
                      <button onClick={() => setExpandedProposal(isExpanded ? null : p.id)}>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {proposal.executive_summary && (
                    <p className="text-sm text-muted-foreground mt-2">{proposal.executive_summary}</p>
                  )}

                  {isExpanded && (
                    <div className="mt-4 space-y-4 border-t pt-4">
                      {proposal.risk_assessment && (
                        <div>
                          <h4 className="text-xs font-semibold text-muted-foreground mb-1">Risk Assessment</h4>
                          <p className="text-sm">{proposal.risk_assessment.suitability_note}</p>
                        </div>
                      )}

                      {proposal.stock_picks?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-muted-foreground mb-2">
                            Stock Picks ({proposal.stock_picks.length})
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead><tr className="text-muted-foreground border-b">
                                <th className="text-left py-1.5 pr-3">Stock</th>
                                <th className="text-left py-1.5 pr-3">Sector</th>
                                <th className="text-right py-1.5 pr-3">Allocation</th>
                                <th className="text-right py-1.5 pr-3">Amount</th>
                                <th className="text-left py-1.5">Cap</th>
                              </tr></thead>
                              <tbody>
                                {proposal.stock_picks.map((s, i) => (
                                  <tr key={i} className="border-b border-border/30">
                                    <td className="py-1.5 pr-3 font-medium">{s.symbol}</td>
                                    <td className="py-1.5 pr-3 text-muted-foreground">{s.sector}</td>
                                    <td className="py-1.5 pr-3 text-right">{s.allocation_pct}%</td>
                                    <td className="py-1.5 pr-3 text-right">{s.amount}</td>
                                    <td className="py-1.5">{s.market_cap_category}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {proposal.implementation_notes && (
                        <div>
                          <h4 className="text-xs font-semibold text-muted-foreground mb-1">Implementation</h4>
                          <p className="text-sm">{proposal.implementation_notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {!proposalsLoading && proposals.length === 0 && (
            <Card><CardContent className="py-8 text-center text-muted-foreground">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No proposals yet. Generate one above.</p>
            </CardContent></Card>
          )}
        </div>
      )}

      {/* Rebalancing Tab */}
      {tab === 'rebalancing' && (
        <div className="space-y-4">
          <Card><CardContent className="pt-4">
            <h3 className="text-sm font-semibold mb-3">Rebalance Portfolio</h3>
            <div className="flex items-center gap-3">
              <select value={selectedMP} onChange={e => setSelectedMP(e.target.value)}
                className="rounded-md border bg-background px-3 py-2 text-sm flex-1 max-w-xs">
                <option value="">AI-suggested allocation</option>
                {modelPortfolios.map(mp => (
                  <option key={mp.id} value={mp.id}>{mp.name} ({mp.risk_bucket})</option>
                ))}
              </select>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                disabled={rebalanceLoading} onClick={handleRebalance}>
                {rebalanceLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                {rebalanceLoading ? 'Calculating...' : 'Calculate Rebalancing'}
              </Button>
            </div>
          </CardContent></Card>

          {rebalanceResult && (
            <>
              {rebalanceResult.mandate_violations?.length > 0 && (
                <Card className="border-red-500/40">
                  <CardContent className="pt-4">
                    <h4 className="text-xs font-semibold text-red-500 mb-2">Mandate warnings</h4>
                    <ul className="text-xs text-red-400 space-y-1">
                      {rebalanceResult.mandate_violations.map((v, i) => (
                        <li key={i}>{v}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
              {rebalanceResult.rationale && (
                <Card><CardContent className="pt-4">
                  <h4 className="text-xs font-semibold text-muted-foreground mb-2">AI Rationale</h4>
                  {typeof rebalanceResult.rationale === 'string'
                    ? <MarkdownMessage content={rebalanceResult.rationale} />
                    : <p className="text-sm">{JSON.stringify(rebalanceResult.rationale)}</p>
                  }
                </CardContent></Card>
              )}

              {rebalanceResult.trades?.length > 0 && (
                <Card><CardContent className="pt-4">
                  <h4 className="text-xs font-semibold text-muted-foreground mb-2">
                    Suggested Trades ({rebalanceResult.trades.length})
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-muted-foreground border-b">
                        <th className="text-left py-1.5 pr-3">Stock</th>
                        <th className="text-left py-1.5 pr-3">Action</th>
                        <th className="text-right py-1.5 pr-3">Current %</th>
                        <th className="text-right py-1.5 pr-3">Target %</th>
                        <th className="text-right py-1.5 pr-3">Change</th>
                        <th className="text-right py-1.5">Est. Value</th>
                      </tr></thead>
                      <tbody>
                        {rebalanceResult.trades.map((t, i) => (
                          <tr key={i} className="border-b border-border/30">
                            <td className="py-1.5 pr-3 font-medium">{t.symbol || t.company_name}</td>
                            <td className="py-1.5 pr-3">
                              <Badge className={`text-[10px] ${
                                t.action === 'Buy' || t.action === 'Increase' ? 'bg-emerald-500/10 text-emerald-600' :
                                t.action === 'Exit' ? 'bg-red-500/10 text-red-600' : 'bg-amber-500/10 text-amber-600'
                              }`}>{t.action}</Badge>
                            </td>
                            <td className="py-1.5 pr-3 text-right">{t.current_weight}%</td>
                            <td className="py-1.5 pr-3 text-right">{t.target_weight}%</td>
                            <td className={`py-1.5 pr-3 text-right ${(t.weight_change || 0) >= 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                              {(t.weight_change || 0) >= 0 ? '+' : ''}{t.weight_change}%
                            </td>
                            <td className="py-1.5 text-right">
                              {t.estimated_value ? `₹${Number(t.estimated_value).toLocaleString('en-IN')}` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent></Card>
              )}
            </>
          )}

          {!rebalanceResult && !rebalanceLoading && (
            <Card><CardContent className="py-8 text-center text-muted-foreground">
              <RefreshCw className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Select a model portfolio or let AI suggest optimal allocation.</p>
            </CardContent></Card>
          )}
        </div>
      )}

      {tab === 'reviews' && (
        <div className="space-y-4">
          <Card><CardContent className="pt-4 flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-xs text-muted-foreground">Period label</label>
              <Input className="mt-1 h-9 w-48" value={reviewPeriod} onChange={e => setReviewPeriod(e.target.value)} />
            </div>
            <Button size="sm" onClick={async () => {
              await generateReview(clientId, reviewPeriod);
              fetchReviews(clientId);
            }}>Generate review</Button>
          </CardContent></Card>
          <div className="space-y-2">
            {reviews.map(r => (
              <Card key={r.id}>
                <CardContent className="pt-3 flex justify-between items-center text-sm">
                  <span>{r.period} — <Badge variant="outline" className="text-[10px]">{r.status}</Badge></span>
                  {r.status === 'draft' && (
                    <Button size="sm" variant="outline" onClick={async () => { await sendReview(r.id); fetchReviews(clientId); }}>Mark sent</Button>
                  )}
                </CardContent>
              </Card>
            ))}
            {reviews.length === 0 && <p className="text-sm text-muted-foreground">No reviews yet.</p>}
          </div>
        </div>
      )}

      {tab === 'attribution' && (
        attributionLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : attribution ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Card><CardContent className="pt-3"><p className="text-xs text-muted-foreground">Portfolio</p><p className="text-lg font-bold">{attribution.total_return}%</p></CardContent></Card>
              <Card><CardContent className="pt-3"><p className="text-xs text-muted-foreground">Benchmark</p><p className="text-lg font-bold">{attribution.benchmark_return}%</p></CardContent></Card>
              <Card><CardContent className="pt-3"><p className="text-xs text-muted-foreground">Allocation</p><p className="text-lg font-bold">{attribution.sector_allocation_effect}%</p></CardContent></Card>
              <Card><CardContent className="pt-3"><p className="text-xs text-muted-foreground">Selection</p><p className="text-lg font-bold">{attribution.stock_selection_effect}%</p></CardContent></Card>
            </div>
            <p className="text-xs text-muted-foreground">Brinson-style attribution vs an equal-weight sector benchmark, decomposed into allocation and selection effects.</p>
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center gap-2 py-12 text-center">
              <PieChart className="w-10 h-10 text-muted-foreground/40" />
              <p className="text-sm font-medium">No attribution data yet</p>
              <p className="text-xs text-muted-foreground max-w-sm">
                Attribution becomes available once this client has holdings and at least one period of performance history.
              </p>
            </CardContent>
          </Card>
        )
      )}

      {tab === 'portal' && (
        <Card>
          <CardContent className="pt-4 space-y-3 text-sm">
            <p className="text-muted-foreground">Share a read-only portal with your client (holdings, approved proposal, sent reviews).</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={async () => { await enablePortal(clientId); fetchClient(clientId); }}>Enable portal</Button>
              <Button size="sm" variant="outline" onClick={async () => { await disablePortal(clientId); fetchClient(clientId); }}>Disable</Button>
            </div>
            {(portalLink || client.portal_enabled) && portalLink && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Link (copy and share)</p>
                <Input readOnly value={portalLink} onFocus={e => e.target.select()} />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Daily Intelligence Tab ── */}
      {tab === 'daily' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold">Daily Portfolio Intelligence</h3>
              <p className="text-xs text-muted-foreground">AI-powered analysis of {client.name}'s portfolio with actionable insights</p>
            </div>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={dailyReportLoading}
              onClick={() => generateDailyReport(clientId)}
            >
              {dailyReportLoading ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />Generating...</> : <><Sparkles className="w-3.5 h-3.5 mr-1" />Generate Now</>}
            </Button>
          </div>

          {dailyReportLoading && !dailyReport && (
            <Card><CardContent className="py-12 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-emerald-500 mb-2" />
              <p className="text-sm text-muted-foreground">Analyzing portfolio, market data, news, and signals...</p>
            </CardContent></Card>
          )}

          {!dailyReportLoading && !dailyReport && (
            <Card><CardContent className="py-12 text-center">
              <Activity className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">No daily report yet. Click "Generate Now" to create the first one.</p>
            </CardContent></Card>
          )}

          {dailyReport && dailyReport.content_json && (() => {
            const rpt = dailyReport.content_json;
            const snap = rpt.portfolio_snapshot || {};
            const holdings = rpt.holdings_analysis || [];
            const actions = rpt.action_items || [];
            const risks = rpt.risk_alerts || [];
            const policies = rpt.policy_regulatory || [];
            return (
              <>
                {/* Report date */}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Activity className="w-3.5 h-3.5" />
                  Report for {rpt.report_date || dailyReport.report_date || 'today'}
                </div>

                {/* Summary */}
                {rpt.summary && (
                  <Card className="border-emerald-500/20 bg-emerald-500/5">
                    <CardContent className="pt-4">
                      <p className="text-sm leading-relaxed">{rpt.summary}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Portfolio snapshot */}
                {snap.total_value > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Card><CardContent className="pt-3 pb-3">
                      <p className="text-[10px] text-muted-foreground">Total Value</p>
                      <p className="text-lg font-bold">₹{(snap.total_value || 0).toLocaleString('en-IN')}</p>
                    </CardContent></Card>
                    <Card><CardContent className="pt-3 pb-3">
                      <p className="text-[10px] text-muted-foreground">Day Change</p>
                      <p className={`text-lg font-bold ${(snap.day_change_pct || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {(snap.day_change_pct || 0) >= 0 ? '+' : ''}{(snap.day_change_pct || 0).toFixed(2)}%
                      </p>
                    </CardContent></Card>
                    <Card><CardContent className="pt-3 pb-3">
                      <p className="text-[10px] text-muted-foreground">Unrealized P&L</p>
                      <p className={`text-lg font-bold ${(snap.unrealized_pnl || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        ₹{(snap.unrealized_pnl || 0).toLocaleString('en-IN')}
                      </p>
                    </CardContent></Card>
                    <Card><CardContent className="pt-3 pb-3">
                      <p className="text-[10px] text-muted-foreground">P&L %</p>
                      <p className={`text-lg font-bold ${(snap.unrealized_pnl_pct || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {(snap.unrealized_pnl_pct || 0) >= 0 ? '+' : ''}{(snap.unrealized_pnl_pct || 0).toFixed(1)}%
                      </p>
                    </CardContent></Card>
                  </div>
                )}

                {/* Action Items */}
                {actions.length > 0 && (
                  <Card className="border-amber-500/20">
                    <CardContent className="pt-4">
                      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" /> Action Items
                      </h4>
                      <div className="space-y-2">
                        {actions.map((a, i) => (
                          <div key={i} className={`flex items-start gap-3 p-2.5 rounded-lg border ${
                            a.action === 'BUY' || a.action === 'INCREASE' ? 'border-emerald-500/20 bg-emerald-500/5' :
                            a.action === 'SELL' || a.action === 'REDUCE' ? 'border-red-500/20 bg-red-500/5' :
                            'border-border'
                          }`}>
                            {(a.action === 'BUY' || a.action === 'INCREASE') ? (
                              <ArrowUpCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                            ) : (a.action === 'SELL' || a.action === 'REDUCE') ? (
                              <ArrowDownCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                            ) : (
                              <TrendingUp className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-sm">{a.action} {a.symbol}</span>
                                <Badge className={`text-[9px] ${
                                  a.urgency === 'high' ? 'bg-red-500/10 text-red-500' :
                                  a.urgency === 'medium' ? 'bg-amber-500/10 text-amber-500' :
                                  'bg-blue-500/10 text-blue-500'
                                }`}>{(a.urgency || 'low').toUpperCase()}</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{a.reason}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Holdings analysis */}
                {holdings.length > 0 && (
                  <Card>
                    <CardContent className="pt-4">
                      <h4 className="text-sm font-semibold mb-3">Holdings Analysis</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b text-muted-foreground">
                              <th className="text-left py-2 pr-3 font-medium">Symbol</th>
                              <th className="text-right py-2 px-2 font-medium">Price</th>
                              <th className="text-right py-2 px-2 font-medium">Day Chg</th>
                              <th className="text-right py-2 px-2 font-medium">Weight</th>
                              <th className="text-left py-2 pl-3 font-medium">Assessment</th>
                            </tr>
                          </thead>
                          <tbody>
                            {holdings.map((h, i) => (
                              <tr key={i} className="border-b border-border/50 hover:bg-secondary/20">
                                <td className="py-2 pr-3">
                                  <span className="font-semibold">{h.symbol}</span>
                                  {h.company_name && h.company_name !== h.symbol && (
                                    <span className="text-[10px] text-muted-foreground block">{h.company_name}</span>
                                  )}
                                </td>
                                <td className="text-right py-2 px-2">₹{(h.current_price || 0).toLocaleString('en-IN')}</td>
                                <td className={`text-right py-2 px-2 font-medium ${(h.day_change_pct || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                  {(h.day_change_pct || 0) >= 0 ? '+' : ''}{(h.day_change_pct || 0).toFixed(2)}%
                                </td>
                                <td className="text-right py-2 px-2">{(h.weight_pct || 0).toFixed(1)}%</td>
                                <td className="py-2 pl-3 text-muted-foreground max-w-xs">{h.assessment}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Macro + Policy */}
                {(rpt.macro_context || policies.length > 0) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {rpt.macro_context && (
                      <Card><CardContent className="pt-4">
                        <h4 className="text-sm font-semibold mb-2">Macro Context</h4>
                        <p className="text-xs text-muted-foreground leading-relaxed">{rpt.macro_context}</p>
                      </CardContent></Card>
                    )}
                    {policies.length > 0 && (
                      <Card><CardContent className="pt-4">
                        <h4 className="text-sm font-semibold mb-2">Policy & Regulatory</h4>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          {policies.map((p, i) => <li key={i} className="flex gap-1.5"><span className="text-amber-500">•</span> {p}</li>)}
                        </ul>
                      </CardContent></Card>
                    )}
                  </div>
                )}

                {/* Risk alerts */}
                {risks.length > 0 && (
                  <Card className="border-red-500/20">
                    <CardContent className="pt-4">
                      <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" /> Risk Alerts
                      </h4>
                      <ul className="text-xs space-y-1">
                        {risks.map((r, i) => <li key={i} className="flex gap-1.5 text-red-400"><span>•</span> {r}</li>)}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Historical reports */}
                {dailyReports.length > 1 && (
                  <Card>
                    <CardContent className="pt-4">
                      <h4 className="text-sm font-semibold mb-2">Report History</h4>
                      <div className="space-y-1">
                        {dailyReports.map(dr => (
                          <div key={dr.id} className="flex items-center justify-between py-1.5 text-xs border-b border-border/50 last:border-0">
                            <span className="font-medium">{dr.report_date}</span>
                            <span className="text-muted-foreground line-clamp-1 max-w-[60%]">{dr.summary || '—'}</span>
                            <Badge variant="outline" className="text-[9px]">{dr.status}</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
